import { PaymentPlan } from 'src/configs/APISchemas/GetQuote';

interface MonthlyPaymentPlanOption {
  title: 'Monthly';
  price: string;
  value: 'MONTHLY';
  description: string;
  duePayment: string;
  periodText: string;
}

interface AnnualPaymentPlanOption {
  title: 'Annually';
  price: string;
  value: 'ANNUAL';
  description: string;
  duePayment: string;
  periodText: string;
  saveAmount: string;
}

export type PaymentPlanOption =
  | MonthlyPaymentPlanOption
  | AnnualPaymentPlanOption;

export function getPaymentPlanOptions(
  paymentPlans: PaymentPlan[],
): PaymentPlanOption[] {
  const annualPaymentData = paymentPlans.find(
    (p) => p.paymentPlanCd === 'Annual',
  );
  const monthlyPaymentData = paymentPlans.find(
    (p) => p.paymentPlanCd === 'Monthly',
  );

  if (annualPaymentData && monthlyPaymentData) {
    const {
      downPaymentAmount: annualDownPayment,
      totalPremiumAmt: annualPremium,
    } = annualPaymentData;

    const {
      downPaymentAmount: monthlyDownPayment,
      totalPremiumAmt: monthlyPremium,
      installmentFeeAmount: installment,
      installmentPaymentAmount,
    } = monthlyPaymentData;

    const annualSaving = calculateAnnualSaving({
      annualPremium,
      installment,
      monthlyPremium,
    });

    return [
      {
        title: 'Annually',
        price: `$${annualPremium}`,
        value: 'ANNUAL',
        description: 'One Time Payment',
        saveAmount: `$${annualSaving}`,
        duePayment: `$${annualDownPayment}`,
        periodText: '/ year',
      },
      {
        title: 'Monthly',
        price: `$${installmentPaymentAmount}`,
        value: 'MONTHLY',
        description: 'Down Payment + 10 installments',
        duePayment: `$${monthlyDownPayment}`,
        periodText: '/ month',
      },
    ];
  }

  return [];
}

/**
 * Calculates the annual saving based on the monthly premium, installment, and annual premium.
 * Formula:
 * ([MONTHLY]'MSI_TotalPremiumAmt' + ([MONTHLY]'MSI_InstallmentFeeAmount' * 10)) - [YEARLY]'MSI_TotalPremiumAmt'
 * @param {Object} options - The options object.
 * @param {string} options.monthlyPremium - The monthly premium.
 * @param {string} options.installment - The installment.
 * @param {string} options.annualPremium - The annual premium.
 * @returns {number} The calculated annual saving.
 */
function calculateAnnualSaving({
  annualPremium,
  installment,
  monthlyPremium,
}: {
  monthlyPremium: string;
  installment: string;
  annualPremium: string;
}): number {
  return (
    parseFloat(monthlyPremium) +
    parseFloat(installment) * 10 -
    parseFloat(annualPremium)
  );
}
